<template>
    <div class="hp">
        <div class="hp-hint">{{ $t('media.hotspot.draw_hint') }}</div>

        <!-- ⚠⚠ **`.hp-stage` 就是座標系**：框的比例是用它的
             `getBoundingClientRect()` 算的，所以它必須**剛好等於圖片的顯示範圍**。
             做法是 `.hp-stage` 用 inline-block 收縮到圖、尺寸限制放在 `<img>` 上
             （見 style 裡的詳細說明與 2026-09-04 修掉的那個 bug）。
             ⚠ 外面不能包固定比例 + `object-fit: contain`——contain 會把圖留白置中，
             於是「容器的百分比」不等於「圖的百分比」，框會整體偏移。
             同一個坑在挑選器的熱區預覽（`.mp-img--free`）已經處理過。 -->
        <div class="hp-box">
        <div ref="stage" class="hp-stage"
            @mousedown="onDown" @mousemove="onMove" @mouseup="onUp" @mouseleave="onUp"
            @touchstart.prevent="onDown" @touchmove.prevent="onMove" @touchend="onUp">
            <img :src="src" class="hp-img" :alt="$t('media.hotspot.image_alt')" draggable="false">

            <!-- 已畫好的框。⚠ 用 % 定位而不是 px：視窗縮放時不會跑掉。 -->
            <div v-if="rect" class="hp-rect" :style="rectStyle"></div>
        </div>
        </div>

        <div class="hp-foot">
            <div v-if="error" class="hp-err">{{ error }}</div>
            <div v-else-if="rect" class="hp-ok">
                {{ $t('media.hotspot.drawn', { w: pctText(rect.w), h: pctText(rect.h) }) }}
            </div>
            <div v-else class="hp-todo">{{ $t('media.hotspot.not_drawn') }}</div>

            <button v-if="rect" type="button" class="hp-clear" @click="clear">
                {{ $t('media.hotspot.redraw') }}
            </button>
        </div>
    </div>
</template>

<script>
import { HOTSPOT_MIN_EDGE_RATIO } from './mediaConstants';

/**
 * 找不同的熱區框選工具 — 見 mangachat-studio-api
 * `docs/plans/MediaLibrary.spec.md` §2.2、[ADR 0004](../../../../../../mangachat-studio-api/docs/adr/0004-hotspots-belong-to-the-asset-pairings-belong-to-the-quest.md)。
 *
 * ⚠ **只框一個矩形**（2026-09-04 的產品決定）。`attributes.targets` 仍然是陣列
 * （學生端會 iterate），只是目前永遠只有一個元素。要開放多個時，這個元件要改成
 * 管理一個陣列，而後端的 `toTargets` 要跟著發序號（現在固定 `id: 1`）。
 *
 * ⚠⚠ **emit 出去的是比例（0–1），不是像素。這一點不能改。**
 * 座標的座標系是**素材存起來之後**的 `width` × `height`，而上傳流程會動它兩次：
 * `processImage` 把長邊縮到 1600、`.rotate()` 依 EXIF 轉向（會把長寬互換）。
 * 所以在瀏覽器裡量出來的像素跟存進 DB 的尺寸不在同一個空間——送比例、
 * 由後端乘上它自己算出的輸出尺寸，是唯一不會偏移的做法。
 *
 * ⚠ 前提是瀏覽器與 sharp 對 EXIF 轉向的解讀一致（前者預設
 * `image-orientation: from-image`、後者明確 `.rotate()`），所以比例是在同一個
 * 轉正後的畫面上量的。**驗收一定要拿一張帶 EXIF 轉向的手機直拍照片試。**
 */
export default {
    name: 'HotspotPicker',
    props: {
        /** 圖片的預覽網址（`URL.createObjectURL(file)`）。 */
        src: { type: String, required: true },
        /** 已畫好的框（v-model），`{x,y,w,h}` 的比例；null = 還沒畫。 */
        value: { type: Object, default: null },
    },
    data() {
        return {
            /// 拖曳的起點（比例）。null = 沒在拖。
            start: null,
            /// 拖曳中的暫時框，放開才 emit
            dragging: null,
        };
    },
    computed: {
        /** 顯示用的框：拖曳中用暫時的，否則用 prop。 */
        rect() {
            return this.dragging || this.value;
        },
        rectStyle() {
            const r = this.rect;
            return {
                left: `${r.x * 100}%`,
                top: `${r.y * 100}%`,
                width: `${r.w * 100}%`,
                height: `${r.h * 100}%`,
            };
        },
        /**
         * 框太小的提示。
         * ⚠ **與後端 `HOTSPOT_MIN_EDGE_RATIO` 同一個數字。** 太小的框學生**點不到**，
         * 而那是靜默的死路——完成判定寫在「點中了」的分支裡，永遠不會過關也不報錯。
         */
        error() {
            const r = this.rect;
            if (!r) {
                return '';
            }
            if (r.w < HOTSPOT_MIN_EDGE_RATIO || r.h < HOTSPOT_MIN_EDGE_RATIO) {
                return this.$t('media.hotspot.err_too_small',
                    { n: Math.round(HOTSPOT_MIN_EDGE_RATIO * 100) });
            }
            return '';
        },
    },
    methods: {
        /**
         * 把事件座標換成 0–1 的比例。
         * ⚠ 用 `.hp-stage` 的 `getBoundingClientRect()`，而那個框就是圖的顯示範圍
         * （見 template 的註解）。⚠ 一定要夾在 0–1：拖到圖外面時不能產生超界的值。
         */
        ratioOf(event) {
            const box = this.$refs.stage.getBoundingClientRect();
            const point = event.touches ? event.touches[0] : event;
            return {
                x: clamp01((point.clientX - box.left) / box.width),
                y: clamp01((point.clientY - box.top) / box.height),
            };
        },

        onDown(event) {
            this.start = this.ratioOf(event);
            this.dragging = { x: this.start.x, y: this.start.y, w: 0, h: 0 };
        },

        onMove(event) {
            if (!this.start) {
                return;
            }
            const now = this.ratioOf(event);
            /// ⚠ 用 min/abs 而不是 `now - start`：老師可能從右下往左上拖，
            /// 那樣寬高會是負的，`left`/`width` 的 CSS 會整個壞掉。
            this.dragging = {
                x: Math.min(this.start.x, now.x),
                y: Math.min(this.start.y, now.y),
                w: Math.abs(now.x - this.start.x),
                h: Math.abs(now.y - this.start.y),
            };
        },

        onUp() {
            if (!this.start) {
                return;
            }
            const drawn = this.dragging;
            this.start = null;
            this.dragging = null;
            /// ⚠ **點一下（沒有拖）不要當成畫了一個框**，否則老師只是想看圖就會
            /// 產生一個 0 大小的框並看到「太小」的錯誤。
            if (!drawn || drawn.w === 0 || drawn.h === 0) {
                return;
            }
            /// ⚠ **太小的框照樣 emit**，讓錯誤訊息顯示出來、送出鍵 disable。
            /// 靜默丟掉的話老師拖了一個小框卻什麼都沒發生，會以為工具壞了。
            this.$emit('input', drawn);
        },

        clear() {
            this.$emit('input', null);
        },

        pctText(v) {
            return Math.round(v * 100);
        },
    },
};

function clamp01(v) {
    return Math.min(1, Math.max(0, v));
}
</script>

<style scoped>
.hp {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 10px;
    background: #fafafa;
}

.hp-hint {
    font-size: 12px;
    color: #607d8b;
    margin-bottom: 8px;
    line-height: 1.5;
}

/* 置中用的外層。⚠ **不要把尺寸限制放在這裡**，那會回到下面說的那個 bug。 */
.hp-box {
    display: flex;
    justify-content: center;
    background: #eceff1;
}

/**
 * ⚠⚠ **這個框必須「剛好等於圖片的顯示範圍」，因為它就是座標系。**
 *
 * `display: inline-block` 讓它收縮到圖片的實際渲染尺寸，所以
 * `getBoundingClientRect()` 量到的就是圖的邊界。
 *
 * ⚠ **2026-09-04 修掉的 bug**：這裡原本是 `display: block` + `max-height: 46vh`
 * + `overflow: hidden`，而圖片是 `width: 100%; height: auto`。後果有兩個，
 * 而第二個嚴重得多：
 * 1. 直立的圖**下半部被裁掉**，老師看不到也框不到。
 * 2. **Y 座標整個算錯**——`box.height` 是被裁切後的 46vh，而圖的實際高度更大，
 *    所以 `(clientY - box.top) / box.height` 除錯了分母。老師在畫面上框的位置
 *    與存進去的比例不一致，而那只有在學生端真的玩過才看得出來。
 *
 * ⚠ 所以尺寸限制要放在**圖片**上（見 `.hp-img` 的 `max-height`），
 * 讓圖等比縮小到整張看得見，框再跟著縮。**不要在這一層加 max-height 或 overflow。**
 *
 * ⚠ 也不能加 padding／border：那會讓這個框與圖片的實際範圍差幾個 px，框整體偏移。
 */
.hp-stage {
    position: relative;
    display: inline-block;
    max-width: 100%;
    line-height: 0;
    cursor: crosshair;
    /* 拖曳時不要選到圖或觸發瀏覽器的圖片拖放 */
    user-select: none;
    -webkit-user-select: none;
    /* 觸控裝置：不要因為拖曳而捲動頁面 */
    touch-action: none;
}

/* ⚠ 尺寸限制在這裡（不是 `.hp-stage`）：`max-height` 讓直立的圖等比縮到整張看得見，
   而 `.hp-stage` 是 inline-block 所以會跟著收縮——座標系仍然等於圖的範圍。 */
.hp-img {
    display: block;
    max-width: 100%;
    max-height: 46vh;
    width: auto;
    height: auto;
    pointer-events: none;
}

/* 外框 + 半透明底兩者都要——純外框在深色圖上看不見。
   顏色與挑選器的熱區預覽（`.mp-target`）一致，老師才認得出是同一個東西。 */
.hp-rect {
    position: absolute;
    border: 2px solid #fb923c;
    background: rgba(251, 146, 60, 0.22);
    border-radius: 2px;
    pointer-events: none;
}

.hp-foot {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
    min-height: 24px;
}

.hp-err {
    font-size: 12px;
    color: #e53935;
    flex: 1 1 auto;
}

.hp-ok {
    font-size: 12px;
    color: #2e7d32;
    flex: 1 1 auto;
}

.hp-todo {
    font-size: 12px;
    color: #90a4ae;
    flex: 1 1 auto;
}

.hp-clear {
    flex: 0 0 auto;
    font-size: 12px;
    color: #1a73e8;
    background: #fff;
    border: 1px solid #90caf9;
    border-radius: 4px;
    padding: 0 10px;
    height: 26px;
    cursor: pointer;
}

.hp-clear:hover {
    background: #f0f6fe;
}
</style>
